// Gemensam uppkopplingsvakt.
//
// `navigator.onLine` räcker inte i en iOS-WebView: den kan säga "online" när
// mobilnätet i praktiken är dött, och `online`-eventet kommer inte alltid när
// telefonen vaknar. Därför väger vi ihop tre källor:
//   1. webbläsarens flagga (snabb men opålitlig)
//   2. appens AppWrapper.network.status() (pollas – API:t har inget event)
//   3. ett litet eko-anrop mot vår egen server (sanningen)
//
// Ekot körs bara när något ser trasigt ut, med gradvis längre paus så att en
// telefon i tunnelbanan inte tömmer batteriet.

export type ConnectivityState = "online" | "reconnecting" | "offline";

export type Connectivity = {
  state: ConnectivityState;
  /** Nätverkstyp från appen när den känner till den ("wifi" | "cellular" | ...). */
  networkType: string | null;
  /** Antal misslyckade försök i rad. */
  attempts: number;
  /** Tidpunkt (ms) då nästa automatiska försök körs, om vi väntar. */
  nextRetryAt: number | null;
  /** Senaste lyckade svarstid mot servern, i millisekunder. */
  latencyMs: number | null;
  /** När uppkopplingen senast tappades. */
  lastOfflineAt: number | null;
};

const HEALTH_URL = "/api/public/health";
const PING_TIMEOUT = 6000;
const NATIVE_POLL_MS = 5000;
const BACKOFF = [2000, 4000, 8000, 15000, 30000];

let current: Connectivity = {
  state: "online",
  networkType: null,
  attempts: 0,
  nextRetryAt: null,
  latencyMs: null,
  lastOfflineAt: null,
};

const listeners = new Set<(value: Connectivity) => void>();
let started = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let nativeTimer: ReturnType<typeof setInterval> | null = null;
let checking = false;

export function getConnectivity(): Connectivity {
  return current;
}

function emit(patch: Partial<Connectivity>) {
  const next = { ...current, ...patch };
  if (
    next.state === current.state &&
    next.networkType === current.networkType &&
    next.attempts === current.attempts &&
    next.nextRetryAt === current.nextRetryAt &&
    next.latencyMs === current.latencyMs
  ) {
    return;
  }
  current = next;
  for (const listener of listeners) listener(current);
}

/** Ett litet anrop mot vår server. Sant = vi når verkligen fram. */
async function pingServer(): Promise<{ ok: boolean; ms: number }> {
  const started = Date.now();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PING_TIMEOUT);
  try {
    const res = await fetch(`${HEALTH_URL}?t=${started}`, {
      method: "GET",
      cache: "no-store",
      // Samma ursprung med kakor – förhandsvisningen blockerar anrop utan dem.
      credentials: "same-origin",
      signal: controller.signal,
    });
    // Även 429/503 betyder att vi har nät – då är det servern som är trött,
    // och det hanteras av felmeddelandena, inte av offlineläget.
    return { ok: res.ok || res.status === 429 || res.status === 503, ms: Date.now() - started };
  } catch {
    return { ok: false, ms: Date.now() - started };
  } finally {
    clearTimeout(timeout);
  }
}

async function nativeOnline(): Promise<{ online: boolean; type: string | null } | null> {
  try {
    const { isAppMode, getNativeNetworkStatus } = await import("@/app-bridge/core/wrapper");
    if (!isAppMode()) return null;
    const status = await getNativeNetworkStatus();
    if (!status) return null;
    return { online: Boolean(status.online), type: status.type ?? null };
  } catch {
    return null;
  }
}

function scheduleRetry() {
  if (retryTimer) clearTimeout(retryTimer);
  const delay = BACKOFF[Math.min(current.attempts, BACKOFF.length - 1)];
  emit({ nextRetryAt: Date.now() + delay });
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void verifyConnection();
  }, delay);
}

/**
 * Kollar uppkopplingen på riktigt. Anropas av vakten själv och av knappen
 * "Försök nu".
 */
export async function verifyConnection(): Promise<boolean> {
  if (typeof window === "undefined") return true;
  if (checking) return current.state === "online";
  checking = true;
  try {
    const native = await nativeOnline();
    if (native) emit({ networkType: native.type });

    const browserSaysOffline = navigator.onLine === false;
    const nativeSaysOffline = native ? !native.online : false;

    if (browserSaysOffline || nativeSaysOffline) {
      goOffline();
      return false;
    }

    const { ok, ms } = await pingServer();
    if (ok) {
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      emit({ state: "online", attempts: 0, nextRetryAt: null, latencyMs: ms });
      return true;
    }
    goOffline();
    return false;
  } finally {
    checking = false;
  }
}

function goOffline() {
  const attempts = current.attempts + 1;
  emit({
    state: attempts === 1 ? "reconnecting" : "offline",
    attempts,
    lastOfflineAt: current.lastOfflineAt ?? Date.now(),
  });
  scheduleRetry();
}

/** Startar vakten. Säkert att anropa flera gånger. */
export function startConnectivityWatch() {
  if (started || typeof window === "undefined") return;
  started = true;

  const onOffline = () => {
    if (current.state === "online") emit({ state: "reconnecting", lastOfflineAt: Date.now() });
    void verifyConnection();
  };
  const onOnline = () => void verifyConnection();

  window.addEventListener("offline", onOffline);
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && current.state !== "online") {
      void verifyConnection();
    }
  });

  // Appen skickar inget nätverks-event, så vi frågar med jämna mellanrum.
  void import("@/app-bridge/core/wrapper").then(({ isAppMode }) => {
    if (!isAppMode() || nativeTimer) return;
    nativeTimer = setInterval(() => {
      void nativeOnline().then((native) => {
        if (!native) return;
        emit({ networkType: native.type });
        if (!native.online && current.state === "online") void verifyConnection();
        if (native.online && current.state !== "online") void verifyConnection();
      });
    }, NATIVE_POLL_MS);
  });

  if (navigator.onLine === false) void verifyConnection();
}

export function subscribeConnectivity(listener: (value: Connectivity) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
